import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Star, Loader2, CheckCircle2, HeartHandshake } from 'lucide-react';

const api = axios.create({ baseURL: '/api/sc-public' });

export default function ScSurveyForm() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    api.get(`/survey/${token}`).then((r) => { setMeta(r.data); if (r.data.alreadyResponded) setDone(true); })
      .catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [token]);

  const brand = meta?.brandColor || '#4f46e5';
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error('Please choose a rating');
    setBusy(true);
    try { await api.post(`/survey/${token}`, { rating, comment }); setDone(true); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not submit — please try again'); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-300" size={28} /></div>;
  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="text-center max-w-md">
        <Star className="mx-auto text-slate-300 mb-3" size={40} />
        <h1 className="text-lg font-bold text-slate-700">Survey link not valid</h1>
        <p className="text-sm text-slate-400 mt-1">This feedback link has expired or isn’t recognised.</p>
      </div>
    </div>
  );
  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="text-center max-w-md bg-white rounded-2xl shadow-sm border border-slate-100 p-10">
        <CheckCircle2 className="mx-auto mb-4" size={48} style={{ color: brand }} />
        <h1 className="text-xl font-bold text-slate-800">Thank you</h1>
        <p className="text-sm text-slate-500 mt-2">Your feedback has been received. We appreciate you taking the time.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          {meta?.logo ? <img src={meta.logo} alt="" className="h-11 w-11 rounded-xl object-contain bg-white border border-slate-100" />
            : <span className="h-11 w-11 rounded-xl flex items-center justify-center text-white" style={{ background: brand }}><HeartHandshake size={22} /></span>}
          <div>
            <h1 className="text-xl font-bold text-slate-800">{meta?.orgName}</h1>
            <p className="text-sm text-slate-400">Your feedback</p>
          </div>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
          <p className="text-sm text-slate-600">{meta?.participantName ? `Hi ${meta.participantName}, how` : 'How'} has your support coordination been? Your feedback helps us improve.</p>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">Your rating</p>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} aria-label={`${n} star${n > 1 ? 's' : ''}`}>
                  <Star size={38} className="transition-transform hover:scale-110"
                    fill={(hover || rating) >= n ? brand : 'none'} color={(hover || rating) >= n ? brand : '#cbd5e1'} strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Anything you’d like to add? (optional)</label>
            <textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-slate-400 focus:ring-0 text-sm" placeholder="What’s working well, or what could be better?" />
          </div>
          <button type="submit" disabled={busy} className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: brand }}>
            {busy ? <Loader2 size={17} className="animate-spin" /> : null}{busy ? 'Submitting…' : 'Submit feedback'}
          </button>
        </form>
        <p className="text-center text-xs text-slate-300 mt-4">Powered by Rostery</p>
      </div>
    </div>
  );
}
